import {
  AfterViewInit,
  booleanAttribute,
  ChangeDetectorRef,
  Component,
  input,
  output,
  SimpleChanges,
  ViewChild,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import type { UntypedFormArray, UntypedFormGroup } from '@angular/forms';
import { UntypedFormControl } from '@angular/forms';

import Flow from '@flowjs/flow.js';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';

import { AttachmentsActions, AttachmentsSelectors } from '@core/attachments-data';
import type { Attachment } from '@core/attachments-types';
import { filterBoolean } from '@core/common-legacy';
import { AttachmentsListComponent } from '@core/components-attachments-list-legacy';
import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import {
  ButtonComponent,
  ButtonLoadingDirective,
  GroupComponent,
  NotificationsController,
  ProtectedControlDirective,
} from '@core/user-interface-legacy';
import {
  asViewRef,
  generateUuid,
  getHumanReadableFileSize,
  MaxFileSize,
  validateMaxFileSize,
} from '@core/utils-legacy';

import { InterviewBaseBoxComponent } from '../interview-base-box/interview-base-box.component';

interface FlowFile {
  name: string;
  file: File;
}

@Component({
  selector: 'rt-interview-file',
  templateUrl: 'interview-file.component.html',
  styleUrls: ['./interview-file.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ProtectedControlDirective,
    GroupComponent,
    ButtonComponent,
    ButtonLoadingDirective,
    TranslatePipe,
    AttachmentsListComponent,
    InterviewBaseBoxComponent,
  ],
})
export class InterviewFileComponent implements AfterViewInit {
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly notificationsCtrl = inject(NotificationsController);
  private readonly i18n = inject(I18nService);
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);

  public readonly questionForm = input<UntypedFormGroup>(undefined!);
  public readonly inputFiles = input<Attachment[]>([]);
  public readonly edit = input<boolean>(false);
  public readonly formSubmissionAttempt = input<boolean>(false);
  public readonly canManageCandidates = input<boolean>(false);
  public readonly hasProtectedControls = input.required<boolean, boolean | string>({
    transform: booleanAttribute,
  });

  public attachmentsChanged = output<any>();
  public filesUploadingChanged = output<boolean>();

  @ViewChild('fileSelectButton', { static: true }) public fileSelectButton: ButtonComponent;

  public flow: any;
  public uploading: number = 0;
  public files: Attachment[] = [];

  private ngOnDestroy$ = new Subject<void>();

  private get attachmentIdsControl(): UntypedFormArray {
    return <UntypedFormArray>this.questionForm().get('attachmentIds');
  }

  public ngOnInit(): void {
    const flowOptions = {
      singleFile: false,
      testChunks: false,
      fileParameterName: 'attachment',
      allowDuplicateUploads: true,
    };

    this.flow = new Flow(flowOptions);
    this.init();
    this.handleFileRemovalSuccess();
  }

  public ngAfterViewInit(): void {
    this.initFormControls();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.inputFiles?.currentValue) {
      this.files = this.inputFiles() || [];
    }
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public dispatchDeleteFile(file: Attachment): void {
    this.store.dispatch(AttachmentsActions.deleteAttachment({ payload: file.id }));
    this.increaseUploading();
  }

  public dispatchDeleteAllFiles(): void {
    if (this.files.length) {
      this.store.dispatch(
        AttachmentsActions.deleteManyAttachments({ payload: this.files.map(file => file.id) }),
      );
      this.increaseUploading();
    }
  }

  private initFormControls(): void {
    this.attachmentIdsControl.clear();
    this.files.forEach(file => this.attachmentIdsControl.push(new UntypedFormControl(file.id)));
    this.cdRef.detectChanges();
  }

  private handleFileRemovalSuccess(): void {
    this.actions$
      .pipe(
        ofType(
          AttachmentsActions.deleteAttachmentSuccess,
          AttachmentsActions.deleteManyAttachmentsSuccess,
        ),
        takeUntil(this.ngOnDestroy$),
      )
      .subscribe(result => {
        result.type === AttachmentsActions.deleteManyAttachmentsSuccess.type
          ? this.deleteAllFiles()
          : this.removeFromList(result.payload.data);
        this.decreaseUploading();
      });
  }

  private init(): void {
    this.flow.assignBrowse(this.fileSelectButton.elRef.nativeElement, false, false);
    this.bindEvents();
  }

  private bindEvents(): void {
    this.flow.on('filesSubmitted', (files: FlowFile[]) => this.submitFiles(files));
  }

  private submitFiles(flowFiles: FlowFile[]): void {
    if (flowFiles && flowFiles.length && !this.uploading) {
      this.uploading = 0;
      this.filesUploadingChanged.emit(false);
      flowFiles.forEach(this.uploadFile.bind(this));
    }
  }

  private uploadFile(flowFile: FlowFile): void {
    const uuid = generateUuid();
    const { file } = flowFile;
    const validSize = validateMaxFileSize(file, MaxFileSize.File50MB);

    if (!validSize) {
      const message = this.i18n.translate('app.common.errors.file_size_limit_exceeded', {
        limit: getHumanReadableFileSize(MaxFileSize.File50MB),
        fileSize: getHumanReadableFileSize(file.size),
      });
      this.notificationsCtrl.error(message);

      return;
    }

    this.increaseUploading();
    this.cdRef.detectChanges();
    this.store.dispatch(
      AttachmentsActions.uploadAttachmentPessimistic({
        payload: {
          file,
          correlationId: uuid,
        },
      }),
    );

    this.store
      .select(AttachmentsSelectors.byId(uuid))
      .pipe(filterBoolean, takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(attachment => {
        this.afterUpload(attachment);
      });

    this.actions$
      .pipe(
        ofType(AttachmentsActions.uploadAttachmentPessimisticFailed),
        take(1),
        takeUntil(this.ngOnDestroy$.asObservable()),
      )
      .subscribe(error => {
        if (uuid === error.payload.correlationId) {
          this.onError();
        }
      });
  }

  private deleteAllFiles(): void {
    this.files = [];
    this.attachmentIdsControl.clear();
    this.attachmentIdsControl.markAsPristine();
    this.attachmentIdsControl.markAsUntouched();
    this.attachmentsChanged.emit(this.files);
  }

  private removeFromList(deletedFile: Attachment): void {
    const files = this.files.filter(file => file.id !== deletedFile.id);

    this.attachmentIdsControl.removeAt(
      this.attachmentIdsControl.value.findIndex((id: number) => id === deletedFile.id),
    );
    this.attachmentIdsControl.markAsDirty();
    this.attachmentsChanged.emit(files);
    this.files = files;

    if (!asViewRef(this.cdRef).destroyed) {
      this.cdRef.detectChanges();
    }
  }

  private afterUpload(file: Attachment): void {
    this.attachmentIdsControl.push(new UntypedFormControl(file.id));
    this.attachmentIdsControl.markAsDirty();
    this.attachmentIdsControl.markAsTouched();
    this.files = [...this.files, file];
    if (!asViewRef(this.cdRef).destroyed) {
      this.cdRef.detectChanges();
    }
    this.attachmentsChanged.emit(this.files);
    this.decreaseUploading();
  }

  private onError(): void {
    this.decreaseUploading();
    if (!asViewRef(this.cdRef).destroyed) {
      this.cdRef.detectChanges();
    }
  }

  private increaseUploading(): void {
    this.uploading++;
    this.filesUploadingChanged.emit(true);
  }

  private decreaseUploading(): void {
    if (this.uploading === 0) return;
    this.uploading--;

    if (this.uploading <= 0) {
      this.filesUploadingChanged.emit(false);
    }
  }
}
